import { inject, Injectable, signal } from '@angular/core';
import {
  Firestore, collection, addDoc, doc, getDocs, query, orderBy, limit,
  startAfter,
  increment,
  where,
  updateDoc,
  DocumentData,
  CollectionReference,
  deleteDoc,
  setDoc,
  getDoc,
  getDocsFromServer
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { UserFullInterface, CategoryInterface, SubCategoryInterface, PeriodsInterface, PaymentMethodsInterface, LanguageInterface, TotalsInterface, UserProfileInterface, PMInterface, TransactionsInterface } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class DbService {
  currentUserDataSig = signal<UserFullInterface | null | undefined>(undefined);
  currentPaymentMethodsSig = signal<PMInterface | undefined>(undefined);
  totalsSig = signal<string | null | undefined>(undefined);
  periodsSig = signal<PeriodsInterface[] | null | undefined>(undefined);
  totalsID = '';
  paginationSize = 10;
  transactions = [];
  lastDoc: DocumentData | undefined;
  firstDoc: DocumentData | undefined;
  subCatsArray: SubCategoryInterface[] = [];

  private db = inject(Firestore);
  private functions = inject(Functions);


  async getCatsAsync(userUID: string): Promise<CategoryInterface[]> {
    const catsArray: CategoryInterface[] = [];
    const collectionPath = `users/${userUID}/categories`
    // const ref: CollectionReference = collection(this.db, collectionPath)
    const ref = collection(this.db, collectionPath)
    const querySnapshot = await getDocs(ref);
    querySnapshot.forEach(async doc => {
      catsArray.push(
        {
          id: doc.id,
          content: doc.data()
        })
    })
    return catsArray;
  }

  async getSubCatListAsync(catID: string, userUID: string): Promise<SubCategoryInterface[]> {
    this.subCatsArray = [];
    const ref: CollectionReference = collection(this.db, 'users', userUID, 'categories', catID, 'subcategories')
    const querySnapshot = await getDocs(ref);
    querySnapshot.forEach(doc => {
      this.subCatsArray.push({
        id: doc.id,
        content: doc.data()
      })
    })
    return this.subCatsArray;
  }

  async deleteCategoryAsync(catID: string, userUID: string): Promise<void> {
    const docPath = `users/${userUID}/categories/${catID}`
    const ref = doc(this.db, docPath)
    await deleteDoc(ref)
  }

  async createCategoryAsync(model: { name: string; label: string; }, userUID: string): Promise<string> {
    const collectionPath = `users/${userUID}/categories`
    const ref: CollectionReference = collection(this.db, collectionPath)
    const data = {
      label: model.label,
      name: model.name,
      sum: 0
    };
    const docRef = await addDoc(ref, data);
    const currentPeriod = parseInt(new Date().toISOString().substring(0, 10).substring(0, 7).replace("-", ""));
    const registrationPeriod = parseInt(this.currentUserDataSig()!.regDate!.substring(0, 7).replace("-", ""));
    const allPeriods = this.listPeriodsBetweenRegistrationAndCategoryCreation(registrationPeriod, currentPeriod);
    for (const pastPeriod of allPeriods) {
      await this.createNewPeriodInCategoriesAsync(pastPeriod, docRef.id, userUID);
    }
    return docRef.id;
  }

  async renameCategoryAsync(model: { label: string; name: string; }, catID: string, userUID: string): Promise<void> {
    const docPath = `users/${userUID}/categories/${catID}`
    const ref = doc(this.db, docPath);
    await updateDoc(ref, { label: model.label, name: model.name });
  }

  async createSubCategoryAsync(model: { name: string; }, catID: string, userUID: string): Promise<string> {
    const collectionPath = `users/${userUID}/categories/${catID}/subcategories`
    const ref: CollectionReference = collection(this.db, collectionPath)
    const data = {
      name: model.name
    };
    const docRef = await addDoc(ref, data);
    return docRef.id;
  }

  async renameSubCategoryAsync(model: { name: string; }, catID: string, subCatID: string, userUID: string): Promise<void> {
    const docPath = `users/${userUID}/categories/${catID}/subcategories/${subCatID}`
    const ref = doc(this.db, docPath);
    await updateDoc(ref, { name: model.name });
  }

  async deleteSubCategoryAsync(catID: string, subCatID: string, userUID: string): Promise<void> {
    const docPath = `users/${userUID}/categories/${catID}/subcategories/${subCatID}`
    const ref = doc(this.db, docPath)
    await deleteDoc(ref)
  }

  async getPeriodsAsync(userUID: string): Promise<PeriodsInterface[]> {
    const output: PeriodsInterface[] = [];
    const collectionPath = `users/${userUID}/periods`
    // const collectionPath = `users/${userUID}/periodsNew`
    const ref: CollectionReference = collection(this.db, collectionPath);
    const querySnapshot = await getDocs(ref);
    querySnapshot.forEach(async doc => {
      output.push(
        {
          id: doc.id,
          content: doc.data()
        })
    })
    return output;
  }

  async cloneBudgetAsync(fromPeriod: number,toPeriod: number, fromPeriodBudget: number, userUID: string) {
    const fn = httpsCallable(this.functions, 'cloneBudget');
    return fn({ fromPeriod: fromPeriod, toPeriod: toPeriod, fromPeriodBudget: fromPeriodBudget, userId: userUID  });
  }

  // async updateBudgetInCategoriesAsync(period: number, amount: number, catID: string, userUID: string): Promise<void> {
  //   const docPath = `users/${userUID}/categories/${catID}`
  //   const fieldPath = `${period}.budget`
  //   const ref = doc(this.db, docPath);
  //   await updateDoc(ref, { [fieldPath]: amount });
  // }

  async updateBudgetInCategoriesAsync(period: number, amount: number, catID: string, userUID: string) {
    const fn = httpsCallable(this.functions, 'updateBudgetInCategories');
    return fn({ userId: userUID, periodId: period, amount: amount, catId: catID });
  }

  // async updateBudgetInPeriodsAsync(periodDocID: string, deltaAmount: number, userUID: string): Promise<void> {
  //   // const docPath = `users/${userUID}/periodsNew/${periodDocID}`
  //   const docPath = `users/${userUID}/periods/${periodDocID}`
  //   const ref = doc(this.db, docPath);
  //   await updateDoc(ref, { budget: increment(deltaAmount) });
  // }

  async updateBudgetInPeriodsAsync(period: number, deltaAmount: number, userUID: string) {
    const fn = httpsCallable(this.functions, 'updateBudgetInPeriods');
    return fn({ userId: userUID, periodId: period, amount: deltaAmount });
  }

  async updateBudget(period: number, catID: string, amount: number, deltaAmount: number, userUID: string) {
    const fn = httpsCallable(this.functions, 'updateBudget');
    return fn({ period: period, catId: catID, newAmount: amount, deltaAmount: deltaAmount, userId: userUID });
  }

  // async replaceBudgetInPeriodsAsync(periodDocID: string, replaceValue: number, userUID: string): Promise<void> {
  //   // const docPath = `users/${userUID}/periodsNew/${periodDocID}`
  //   const docPath = `users/${userUID}/periods/${periodDocID}`
  //   const ref = doc(this.db, docPath);
  //   await updateDoc(ref, { budget: replaceValue });
  // }

  async replaceBudgetInPeriodsAsync(period: number, newAmount: number, userUID: string) {
    const fn = httpsCallable(this.functions, 'replaceBudgetInPeriods');
    return fn({ userId: userUID, periodId: period, amount: newAmount });
  }

  async getTransactionListMatchingPhraseAsync(column: string, phrase: string, userUID: string): Promise<DocumentData[]> {
    const output: DocumentData[] | PromiseLike<DocumentData[]> | { _id: string; }[] = [];
    const collectionPath = `users/${userUID}/transactionsNew`
    const ref: CollectionReference = collection(this.db, collectionPath);
    if (column == 'Category') {
      const customQuery = query(ref, where(column, "==", phrase), orderBy('ID', 'desc'), limit(this.paginationSize))
      const querySnapshot = await getDocs(customQuery);
      querySnapshot.forEach(doc => {
        output.push({ ...doc.data(), _id: doc.id })
      });
    } else if (column == 'Amount') {
      const amount = parseFloat(phrase)
      const customQuery = query(ref, where(column, ">=", amount * 0.95), where(column, "<", amount * 1.05), orderBy('Date', 'desc'), limit(this.paginationSize));
      const querySnapshot = await getDocs(customQuery);
      querySnapshot.forEach(doc => {
        output.push({ ...doc.data(), _id: doc.id })
      });
    }
    return output;
  }

  async getTransactionListAsync(type: string, period: number, userUID: string): Promise<TransactionsInterface[]> {
    const output = [];
    const collectionPath = `users/${userUID}/transactionsNew`
    const ref: CollectionReference = collection(this.db, collectionPath);
    if (type === 'first') {
      const first = query(ref, where('period', "==", period), orderBy('ID', 'desc'), limit(this.paginationSize));
      const querySnapshot = await getDocs(first);
      querySnapshot.forEach(async (doc) => {
        output.push({ ...doc.data(), _id: doc.id });
      });
      if (querySnapshot.docs.length == 0) {
        this.lastDoc = undefined;
        this.firstDoc = undefined;
      } else {
        this.lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        this.firstDoc = querySnapshot.docs[0];
      }
    } else if (type === 'next') {
      const next = query(ref, where('period', "==", period), orderBy('ID', 'desc'), startAfter(this.lastDoc), limit(this.paginationSize));
      const querySnapshot = await getDocs(next);
      querySnapshot.forEach(async (doc) => {
        output.push({ ...doc.data(), _id: doc.id });
      });
      this.lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    }
    return output
  }

  // async deleteTransactionAsync(docID: string, userUID: string): Promise<void> {
  //   const docPath = `users/${userUID}/transactionsNew/${docID}`
  //   const ref = doc(this.db, docPath)
  //   await deleteDoc(ref)
  // }

  async deleteTransactionAsync(docID: string, userUID: string) {
    const fn = httpsCallable(this.functions, 'deleteTransaction');
    return fn({ userId: userUID, docID: docID });
  }

  // async setTotalsAsync(command: string, userUID: string): Promise<void> {
  //   const docPath = `users/${userUID}/totals/${this.totalsSig()}`
  //   const ref = doc(this.db, docPath)
  //   if (command == 'add') {
  //     updateDoc(ref, { total: increment(1), ultimateTotal: increment(1) });
  //   } else if (command == 'subtract') {
  //     updateDoc(ref, { total: increment(-1) });
  //   }
  // }

  async updateExpenseInPeriodsAsync(type: number, amount: number, periodDocID: string, userUID: string): Promise<void> {
    // const docPath = `users/${userUID}/periodsNew/${periodDocID}`
    const docPath = `users/${userUID}/periods/${periodDocID}`
    const ref = doc(this.db, docPath);
    await updateDoc(ref, { expense: increment(amount), transactions: increment(type) });
  }

  async updateExpenseInCategoriesAsync(period: number, amount: number, catID: string, userUID: string): Promise<void> {
    const docPath = `users/${userUID}/categories/${catID}`;
    const fieldPath = `${period}.expense`;
    const ref = doc(this.db, docPath);
    await updateDoc(ref, { [fieldPath]: increment(amount) });
  }

  async getPaymentMethodsAsync(userUID: string): Promise<PMInterface> {
    const collectionPath = `users/${userUID}/paymentMethods`
    const ref: CollectionReference = collection(this.db, collectionPath);
    const querySnapshot = await getDocs(ref);
    const doc = querySnapshot.docs[0];
    return doc.data() as PMInterface
  }

  // async addTransactionAsync(transaction: unknown, userUID: string): Promise<void> {
  //   let updatedTransaction = transaction;
  //   updatedTransaction['Category'] = transaction['Category']['content']['name'];
  //   updatedTransaction['SubCategory'] = transaction['SubCategory']['content']['name'];
  //   const collectionPath = `users/${userUID}/transactionsNew`
  //   const ref: CollectionReference = collection(this.db, collectionPath)
  //   await addDoc(ref, updatedTransaction);
  // }

  async addTransactionAsync(transaction: TransactionsInterface, userUID: string) {
    let updatedTransaction = transaction;
    updatedTransaction['Category'] = transaction['Category']['content']['name'];
    updatedTransaction['SubCategory'] = transaction['SubCategory']['content']['name'];
    const fn = httpsCallable(this.functions, 'addTransaction');
    return fn({ userId: userUID, model: updatedTransaction });
  }

  async updatePaymentMethodsAsync(method: string, source: string, action: string, userUID: string, value?: string) {
    const docPath = `users/${userUID}/paymentMethods/${userUID}`;
    const fieldPathMethod = `method${method}.name`;
    const fieldPathSource = `method${method}.source${source}.name`;
    const ref = doc(this.db, docPath);
    if (action === 'rename') {
      if (source === '0') {
        await updateDoc(ref, { [fieldPathMethod]: value });
        const snapshot = await getDoc(ref)
        const paymentMethods = snapshot.data() as PMInterface;
        if (paymentMethods) {
          this.currentPaymentMethodsSig.set(paymentMethods);
        }

      } else if (source === '1' || source === '2') {
        await updateDoc(ref, { [fieldPathSource]: value });
        const snapshot = await getDoc(ref)
        const paymentMethods = snapshot.data() as PMInterface;
        if (paymentMethods) {
          this.currentPaymentMethodsSig.set(paymentMethods);
        }
      }

    }
    if (action === 'delete') {
      let newDefault = '0.0';
      if (this.currentPaymentMethodsSig().default == method + '.' + source) {
        if (this.currentPaymentMethodsSig().method1.source1 && method + '.' + source != '1.1') {
          newDefault = '1.1';
        } else {
          if (this.currentPaymentMethodsSig().method1.source2 && method + '.' + source != '1.2') {
            newDefault = '1.2';
          }
          else {
            if (this.currentPaymentMethodsSig().method2.source1 && method + '.' + source != '2.1') {
              newDefault = '2.1';
            }
            else {
              if (this.currentPaymentMethodsSig().method2.source2 && method + '.' + source != '2.2') {
                newDefault = '2.2';
              }
            }
          }
        }
      } else {
        newDefault = this.currentPaymentMethodsSig().default;
      }
      if (source === '1' || source === '2') {
        await updateDoc(ref, { [fieldPathSource]: userUID, default: newDefault });
        const snapshot = await getDoc(ref)
        const paymentMethods = snapshot.data() as PMInterface;
        if (paymentMethods) {
          this.currentPaymentMethodsSig.set(paymentMethods);
        }
      }
    }
    if (action === 'setDefault') {
      const defaultString = method + '.' + source;
      await updateDoc(ref, { default: defaultString });
      const snapshot = await getDoc(ref)
      const paymentMethods = snapshot.data() as PMInterface;
      if (paymentMethods) {
        this.currentPaymentMethodsSig.set(paymentMethods);
      }
    }
    else if (action === 'createSource') {
      const sourceRef = 'source' + source;
      await updateDoc(ref, { [fieldPathSource]: sourceRef });
      const snapshot = await getDoc(ref)
      const paymentMethods = snapshot.data() as PMInterface;
      if (paymentMethods) {
        this.currentPaymentMethodsSig.set(paymentMethods);
      }
    }
  }

  async CreateUserDocumentAfterRegistrationAsync(user, username: string, language: string, curr: string, userUID: string): Promise<void> {
    const date: Date = new Date()

    const data: UserFullInterface = {
      uid: userUID,
      email: user.email,
      name: user.name,
      surname: user.surname,
      username: username,
      regDate: date.getFullYear().toString().concat('-'.concat((date.getMonth() + 1).toString())).concat('-'.concat(date.getDate().toString())),
      transCounter: 0,
      plan: user.plan,
      lang: language,
      currency: curr,
      newUser: true
    };
    this.currentUserDataSig.set({
      currency: curr,
      email: user.email,
      lang: language,
      name: user.name,
      newUser: true,
      username: username,
      plan: user.plan,
      regDate: date.getFullYear().toString().concat('-'.concat((date.getMonth() + 1).toString())).concat('-'.concat(date.getDate().toString())),
      surname: user.surname,
      transCounter: 0,
      uid: userUID
    });
    const docPath = `users/${userUID}`;
    const ref = doc(this.db, docPath);

    if (data.lang === 'pl') {
      data.name = 'Jan';
      data.surname = 'Kowalski';
    } else {
      data.name = 'John';
      data.surname = 'Smith';
    }
    data.plan = 'Free';
    data.newUser = true;

    await setDoc(ref, data, { merge: true });
  }

  async createAllPaymentMethodsAsync(value: string, userUID: string): Promise<void> {
    const docPath = `users/${userUID}/paymentMethods/${userUID}`;
    const ref = doc(this.db, docPath);
    if (value == 'pl') {
      const data = {
        'default': "1.1",
        'method1': {
          'name': 'Metoda1',
          'source1': {
            'name': 'Źródło1'
          },
          'source2': {
            'name': 'Źródło2'
          },
        },
        'method2': {
          'name': 'Metoda2',

          'source1': {
            'name': 'Źródło1'
          },
          'source2': {
            'name': 'Źródło2'
          }
        }
      }
      await setDoc(ref, data, { merge: true })
    } else if (value == 'en') {
      const data = {
        'default': "1.1",
        'method1': {
          'name': 'Method1',
          'source1': {
            'name': 'Source1'
          },
          'source2': {
            'name': 'Source2'
          },
        },
        'method2': {
          'name': 'Method2',

          'source1': {
            'name': 'Source1'
          },
          'source2': {
            'name': 'Source2'
          }
        }
      }
      await setDoc(ref, data, { merge: true })
    }
    const paymentMethods = await this.getPaymentMethodsAsync(userUID);
    if (paymentMethods) {
      this.currentPaymentMethodsSig.set(paymentMethods);
    }
  }

  // async addPeriodInPeriodsAsync(period: number, userUID: string): Promise<void> {
  //   const collectionPath = `users/${userUID}/periodsNew`;
  //   const ref: CollectionReference = collection(this.db, collectionPath)
  //   const data = {
  //     budget: 0,
  //     expense: 0,
  //     transactions: 0,
  //     periodID: period
  //   }
  //   await addDoc(ref, data);
  // }

  async addPeriodInPeriodsAsync(period: number, userUID: string) {
    const fn = httpsCallable(this.functions, 'addPeriodInPeriods');
    return fn({ userId: userUID, periodId: period });
  }


  // async createNewPeriodInCategoriesAsync(period: number, catID: string, userUID: string): Promise<void> {
  //   const docPath = `users/${userUID}/categories/${catID}`;
  //   const fieldPathExpense = `${period}.expense`;
  //   const fieldPathBudget = `${period}.budget`;
  //   const ref = doc(this.db, docPath);
  //   await updateDoc(ref, { [fieldPathExpense]: increment(0), [fieldPathBudget]: increment(0) });
  // }
  async createNewPeriodInCategoriesAsync(period: number, catID: string, userUID: string) {
    const fn = httpsCallable(this.functions, 'addPeriodInCategories');
    return fn({ userId: userUID, periodId: period, catId: catID });
  }

  async getUserDocsAsync(userUID: string) {
    const docPath = `users/${userUID}`;
    const ref = doc(this.db, docPath);
    const querySnapshot = await getDoc(ref);
    return querySnapshot.data();
  }

  async getTotalsAsync(userUID: string): Promise<TotalsInterface> {
    const docPath = `users/${userUID}/totals/counters`
    const ref = doc(this.db, docPath);
    const querySnapshot = await getDoc(ref);
    return querySnapshot.data() as TotalsInterface;
  }


  listPeriodsBetweenRegistrationAndCategoryCreation(regPeriod: number, currentPeriod: number) {
    const theList = []
    let i = regPeriod
    while (i <= currentPeriod) {
      if ((i - (Math.floor(i / 100) * 100)) > 12) {
        i = (Math.floor(i / 100) + 1) * 100 + 1
      } else {
        theList.push(i);
        i++;
      }
    }
    return theList
  }

  async updateUserDataAsync(model, userUID: string) {
    const currentUser = this.currentUserDataSig();
    const docPath = `users/${userUID}`
    const ref = doc(this.db, docPath);
    const formdata = {
      uid: currentUser!.uid,
      email: currentUser!.email!,
      name: model.name,
      surname: model.surname,
      username: model.username,
      photoURL: model.photoURL,
      plan: currentUser!.plan,
      lang: model.lang,
      currency: model.currency,
      newUser: currentUser!.newUser
    };

    if (!formdata.photoURL) {
      formdata.photoURL = './../../assets/man.png';
    }
    if (!formdata.name) {
      if (!currentUser!.name) {
        formdata.name = 'John';
      } else {
        formdata.name = currentUser!.name;
      }
    }
    if (!formdata.surname) {
      if (!currentUser!.surname) {
        formdata.surname = 'Smith';
      } else {
        formdata.surname = currentUser!.surname;
      }
    }
    if (!formdata.username) {
      if (!currentUser!.username) {
        formdata.username = 'Johnny';
      } else {
        formdata.username = currentUser!.username;
      }
    }
    if (!formdata.currency) {
      if (!currentUser!.currency) {
        formdata.currency = 'PLN';
      } else {
        formdata.currency = currentUser!.currency;
      }
    }
    if (!formdata.lang) {
      if (!currentUser!.lang) {
        formdata.lang = 'PLN';
      } else {
        formdata.lang = currentUser!.lang;
      }
    }
    await updateDoc(ref, formdata);
    this.currentUserDataSig.set({
      currency: formdata?.['currency'],
      email: formdata?.['email'],
      lang: formdata?.['lang'],
      name: formdata?.['name'],
      newUser: formdata?.['newUser'],
      username: formdata?.['username'],
      plan: formdata?.['plan'],
      regDate: currentUser?.regDate!,
      surname: formdata?.['surname'],
      transCounter: currentUser?.transCounter!,
      uid: formdata?.['uid']
    });

  }
}